import type { Strapi4RequestParams } from '@nuxtjs/strapi'
import type { FetchOptions } from 'ofetch'
import {
  EMPLOYERS_REST_SEGMENT,
  type EmployerDetailResponse,
  type EmployerListQueryParams,
  type EmployerListResponse,
  type EmployerSpontaneousBoardResponse,
  type EmployerSpontaneousBoardUpdateData
} from '@profilpublic/api-contract/employer'

const RESOURCE = 'employers' as const

/**
 * Strapi `employers` collection helpers typed against `@profilpublic/api-contract/employer`.
 * Uses `useStrapi4()` for `find` / `findOne` and `useStrapiClient()` for REST paths under `/employers`.
 * (Usage volontairement limité à quelques fichiers de test pour l’instant.)
 */
export function useEmployersApi() {
  const { find, findOne } = useStrapi4()
  const client = useStrapiClient()

  function toStrapiParams(params?: EmployerListQueryParams): Strapi4RequestParams | undefined {
    return params as unknown as Strapi4RequestParams | undefined
  }

  function findEmployers(params?: EmployerListQueryParams, fetchOptions?: FetchOptions) {
    return find<EmployerListResponse>(RESOURCE, toStrapiParams(params), fetchOptions)
  }

  function findOneEmployer(idOrSlug: string | number, params?: Strapi4RequestParams, fetchOptions?: FetchOptions) {
    return findOne<EmployerDetailResponse>(RESOURCE, idOrSlug, params, fetchOptions)
  }

  function recordEmployerView(employerId: string | number, body: { url: string }, options?: FetchOptions) {
    return client(`${EMPLOYERS_REST_SEGMENT}/${employerId}/view`, {
      method: 'POST',
      body,
      ...options
    })
  }

  function updateEmployer(employerId: string | number, body: FormData, options?: FetchOptions) {
    return client(`${EMPLOYERS_REST_SEGMENT}/${employerId}`, {
      method: 'PUT',
      body,
      ...options
    })
  }

  function getSpontaneousBoard(employerId: string | number, options?: FetchOptions) {
    return client<EmployerSpontaneousBoardResponse>(`${EMPLOYERS_REST_SEGMENT}/${employerId}/spontaneous-board`, options)
  }

  function updateSpontaneousBoard(employerId: string | number, data: EmployerSpontaneousBoardUpdateData, options?: FetchOptions) {
    return client<EmployerSpontaneousBoardResponse>(`${EMPLOYERS_REST_SEGMENT}/${employerId}/spontaneous-board`, {
      method: 'PUT',
      body: { data },
      ...options
    })
  }

  return {
    collection: RESOURCE,
    basePath: EMPLOYERS_REST_SEGMENT,
    findEmployers,
    findOneEmployer,
    recordEmployerView,
    updateEmployer,
    getSpontaneousBoard,
    updateSpontaneousBoard,
    client
  }
}
